import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import { TextField, InputAdornment, IconButton, TextFieldProps } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { NumericFormat } from 'react-number-format';

type CustomTextFieldProps = {
    name: string;
    control: any;
    rules?: any;
    limit?: number;
    defaultValue?: string;
    errorMessage?: string;
    showSign?: boolean;
    onChange?: (value: any) => void;
} & TextFieldProps;

const CustomTextField: React.FC<CustomTextFieldProps> = ({
    name,
    control,
    rules,
    limit = null,
    defaultValue = "",
    errorMessage,
    type = "text",
    showSign = false,
    onChange,
    ...rest
}) => {
    const [showPassword, setShowPassword] = useState(false);

    const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

    const renderEndAdornment = () => {
        if (showSign) {
            return <InputAdornment position="end">₪</InputAdornment>;
        }
        if (type === "password") {
            return (
                <InputAdornment position="end">
                    <IconButton
                        aria-label="toggle password visibility"
                        onClick={togglePasswordVisibility}
                        edge="end"
                    >
                        {showPassword ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                    </IconButton>
                </InputAdornment>
            );
        }
        return null;
    };

    const commonInputProps = {
        endAdornment: renderEndAdornment(),
        sx: {
            height: { xs: '40px', sm: '47px' },
            borderRadius: '15px',
            ...(type.includes("email") && {
                direction: 'rtl',
                "& .MuiInputBase-input": {
                    textAlign: "left",
                },
            }),
        },
    };

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            rules={rules}
            render={({ field, fieldState }) => (
                showSign ? (
                    <NumericFormat
                        {...field}
                        {...rest}
                        customInput={TextField}
                        thousandSeparator=","
                        valueIsNumericString
                        error={fieldState.invalid}
                        variant="outlined"
                        isAllowed={(values) => {
                            const { value } = values;
                            return value.length <= 8;
                        }}
                        onKeyPress={(event: any) => {
                            if (event?.key === '-' || event?.key === '+') {
                                event.preventDefault();
                            }
                        }}
                        InputProps={{
                            ...rest.InputProps,
                            ...commonInputProps,
                        }}
                        onValueChange={(values) => {
                            field.onChange(values.value);
                            if (onChange) {
                                onChange(values.value);
                            }
                        }}
                    />
                ) : (
                    <TextField
                        {...field}
                        {...rest}
                        onFocus={(e) =>
                            e.target.addEventListener("wheel", function (e) {
                                e.preventDefault();
                            }, { passive: false })
                        }
                        type={type === "password" ? (!showPassword ? "password" : "text") : type}
                        error={fieldState.invalid}
                        variant="outlined"
                        inputProps={{
                            maxLength: limit,
                            ...(type === "number" && {
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                            }),
                        }}
                        InputProps={{
                            ...rest.InputProps,
                            ...commonInputProps,
                        }}
                        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                            if (type === "number") {
                                const invalidChars = ["-", "+", "e", ".", ","];
                                if (invalidChars.includes(e.key)) {
                                    e.preventDefault();
                                }
                            } else if (type.includes("email")) {
                                const invalidChars = /[^a-zA-Z0-9@._%+\-]/;
                                if (invalidChars.test(e.key)) {
                                    e.preventDefault();
                                }
                            }
                        }}
                        onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                            if (type === "number") {
                                // Remove any non-numeric characters
                                e.target.value = e.target.value.replace(/[^0-9]/g, "");
                            } else if (type.includes("email")) {
                                const value = e.target.value;
                                const sanitizedValue = value.replace(/[^a-zA-Z0-9@._%+\-]/g, '');
                                if (sanitizedValue !== value) {
                                    e.target.value = sanitizedValue;
                                }
                            }
                            if (limit && e.target.value.length > limit) {
                                e.target.value = e.target.value.slice(0, limit);
                            }
                        }}
                        onChange={(e) => {
                            field.onChange(e);
                            if (onChange) {
                                onChange(e.target.value);
                            }
                        }}
                    />
                )
            )}
        />
    );
};


export default CustomTextField;
